import { EateryDiscount, EateryInfo, Gene } from 'api'
import { EATERIES_VIEWED } from 'app'
import { SESSION_USER_SUMMARY_KEY } from 'store/dbLabels'
import { Coords, SEARCH_STATE, SearchOpts } from 'store/search'

export function trackSearchEvent(search: SEARCH_STATE, opt?: SearchOpts) {
    if (!globalThis.umami || !umami) return

    const extras: SearchOpts = opt || {
        forceNoFilters: false,
    }

    umami.track('search-searching-event', {
        ...extras,

        decor: search.activeDecor,
        audience: search.activeAudience,
        category: search.activeGD,

        location: search.activeLoc?.kind ?? 'none',

        query: search.query,

        filters: {
            open: search.filters.open,
            random: search.filters.random,
            stars: search.filters.stars,
            h_menu: search.filters.h_menu,
            h_photos: search.filters.h_photos,
            is_247: search.filters.is_247,
            never_went: search.filters.never_went,
            holds_bday: search.filters.holds_bday,
            discount: search.additionalFilters.discount,
        },
        sort: search.sort,

        event: search.activeEvent,
    })
}
export function trackSearchNoResult(search: SEARCH_STATE, coords: Coords) {
    if (!globalThis.umami || !umami) return

    umami.track('search-no-result-event', {
        decor: search.activeDecor,
        audience: search.activeAudience,
        category: search.activeGD,

        location: search.activeLoc?.kind ?? 'none',
        coords,

        query: search.query,

        filters: {
            open: search.filters.open,
            random: search.filters.random,
            stars: search.filters.stars,
            h_menu: search.filters.h_menu,
            h_photos: search.filters.h_photos,
            is_247: search.filters.is_247,
            never_went: search.filters.never_went,
            holds_bday: search.filters.holds_bday,
            discount: search.additionalFilters.discount,
        },

        sort: search.sort,

        // Useful to keep even though you didn't explicitly ask for it
        event: search.activeEvent,
    })
}
export function trackCloseSearchWithoutEateryViewEvent() {
    if (!globalThis.umami || !umami) return

    umami.track('search-searching-close-without-view')
}

export function trackDiscountBuy(eatery: EateryInfo, discount: EateryDiscount) {
    if (!globalThis.umami || !umami) return

    umami.track('eatery-discount-buy', {
        eatery_name: eatery.name,
        eatery_gene: eatery.gene,

        discount_percentage: discount.percentage,
        discount_price: discount.rob_price,
        discount_min_order: discount.min_order,
        discount_max_amount: discount.max_amount,
        discount_available: discount.available,
        discount_starts_at: discount.starts_at,
        discount_expires_at: discount.expires_at,
    })
}

export function trackEateryViewSource(
    gene: Gene,
    source: 'map' | 'search' | 'home'
) {
    if (!globalThis.umami || !umami) return
    if (!gene) return

    umami.track(`eatery-view-source-${source}`, {
        eatery_gene: gene,
    })
}

export function trackEateryOpened(gene: Gene) {
    if (!globalThis.umami || !umami) return
    if (!gene) return

    umami.track('eatery-view-opened', {
        eatery_gene: gene,
    })
}

export function trackMenuCartOpened(gene: Gene) {
    if (!globalThis.umami || !umami) return
    if (!gene) return

    umami.track('menu-cart-opened', {
        eatery_gene: gene,
    })
}

export function trackMenuItemInfoOpened(gene: Gene) {
    if (!globalThis.umami || !umami) return
    if (!gene) return

    umami.track('menu-item-info-opened', {
        eatery_gene: gene,
    })
}

export function trackRateEateryFromReminder(gene: Gene) {
    if (!globalThis.umami || !umami) return
    if (!gene) return

    umami.track('eatery-review-from-reminder', {
        eatery_gene: gene,
    })
}

type SessionSummary = {
    eateries_viewed: number

    searched: boolean
    search_count: number

    used_filters: boolean

    navigated: boolean
    saved: boolean
    called: boolean
}

const DEFAULT_SESSION_SUMMARY: SessionSummary = {
    eateries_viewed: 0,

    searched: false,
    search_count: 0,

    used_filters: false,

    navigated: false,
    saved: false,
    called: false,
}

let USER_SUMMARY: SessionSummary = {
    ...DEFAULT_SESSION_SUMMARY,
}

/* -------------------------------------------------- */
/* Storage                                             */
/* -------------------------------------------------- */
function saveSessionSummary() {
    if (typeof window === 'undefined') return

    try {
        sessionStorage.setItem(
            SESSION_USER_SUMMARY_KEY,
            JSON.stringify(USER_SUMMARY)
        )
    } catch {
        // Ignore storage errors
    }
}

export function loadSessionSummary() {
    if (typeof window === 'undefined') return

    try {
        const saved = sessionStorage.getItem(SESSION_USER_SUMMARY_KEY)

        if (!saved) {
            USER_SUMMARY = { ...DEFAULT_SESSION_SUMMARY }
            return
        }

        const parsed = JSON.parse(saved)

        if (!parsed || typeof parsed !== 'object') {
            USER_SUMMARY = { ...DEFAULT_SESSION_SUMMARY }
            return
        }

        USER_SUMMARY = {
            ...DEFAULT_SESSION_SUMMARY,
            ...parsed,
        }

        // Make sure numeric values are actually numbers
        USER_SUMMARY.eateries_viewed =
            typeof USER_SUMMARY.eateries_viewed === 'number' &&
            Number.isFinite(USER_SUMMARY.eateries_viewed)
                ? Math.max(0, USER_SUMMARY.eateries_viewed)
                : 0

        USER_SUMMARY.search_count =
            typeof USER_SUMMARY.search_count === 'number' &&
            Number.isFinite(USER_SUMMARY.search_count)
                ? Math.max(0, USER_SUMMARY.search_count)
                : 0
    } catch {
        USER_SUMMARY = { ...DEFAULT_SESSION_SUMMARY }
        sessionStorage.removeItem(SESSION_USER_SUMMARY_KEY)
    }
}

/* -------------------------------------------------- */
/* Update                                             */
/* -------------------------------------------------- */

export function sessionSummaryUpdate(
    key: keyof SessionSummary,
    value: boolean | number
) {
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return

        const current = USER_SUMMARY[key]

        if (typeof current !== 'number') return

        USER_SUMMARY[key] = Math.max(0, current + value) as never
    } else {
        const current = USER_SUMMARY[key]

        if (typeof current !== 'boolean') return

        USER_SUMMARY[key] = value as never
    }

    saveSessionSummary()
}

/* -------------------------------------------------- */
/* Semantic helpers                                   */
/* -------------------------------------------------- */

export function updateTrackSearchForSession() {
    USER_SUMMARY.searched = true
    USER_SUMMARY.search_count += 1

    saveSessionSummary()
}
export function updateTrackFilterForSession() {
    USER_SUMMARY.used_filters = true

    saveSessionSummary()
}
export function updateTrackNavigationForSession() {
    USER_SUMMARY.navigated = true

    saveSessionSummary()
}
export function updateTrackSaveForSession() {
    USER_SUMMARY.saved = true

    saveSessionSummary()
}
export function updateTrackCallForSession() {
    USER_SUMMARY.called = true

    saveSessionSummary()
}

/* -------------------------------------------------- */
/* Summary                                             */
/* -------------------------------------------------- */

export function trackUserSessionSummary() {
    if (!globalThis.umami || !umami) return

    const data: SessionSummary = {
        ...USER_SUMMARY,
        eateries_viewed: EATERIES_VIEWED.size,
    }

    umami.track('user-session-summary', {
        ...data,
    })

    resetUserSessionSummary()
}
export function getUserSessionSummary(): SessionSummary {
    return {
        ...USER_SUMMARY,
    }
}
export function resetUserSessionSummary() {
    USER_SUMMARY = {
        ...DEFAULT_SESSION_SUMMARY,
    }

    if (typeof window !== 'undefined') {
        try {
            sessionStorage.removeItem(SESSION_USER_SUMMARY_KEY)
        } catch {
            // Ignore storage errors
        }
    }
}
